import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { submitHelpCenterComplaint } from "../../services/help";
import "../../styles/Help.css";

// ── Icons ─────────────────────────────────────────────
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const TrackIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);
const CancelIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);
const ReportIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const ArrowIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const RocketIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
  </svg>
);
const DesktopIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);
const FaqIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const SendIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const COMPLAINT_TYPES = [
  "Order not delivered",
  "Wrong item received",
  "Damaged product",
  "Refund not received",
  "Payment issue",
  "Account problem",
  "Other",
];

const TOPICS = [
  { Icon: RocketIcon,  title: "Getting Started", desc: "Learn how to quickly get the most out of Easy World.", count: 12 },
  { Icon: DesktopIcon, title: "Desktop App",      desc: "Get to know the basics of the desktop app for shopping.", count: 22 },
  { Icon: FaqIcon,     title: "FAQs",             desc: "Answers to the most commonly asked questions.", count: 6 },
];

// ── Component ──────────────────────────────────────────
export default function Help() {
  const navigate = useNavigate();
  const [searchQuery,  setSearchQuery]  = useState("");
  const [showModal,    setShowModal]    = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [complainType, setComplainType] = useState("");
  const [issue,        setIssue]        = useState("");

  // Quick actions
  const QUICK_ACTIONS = [
    {
      Icon: TrackIcon,
      title: "Track Order",
      desc: "Check your delivery status in real time",
      onClick: () => navigate("/orders"),
    },
    {
      Icon: CancelIcon,
      title: "Cancel Order",
      desc: "Cancel an existing order easily",
      onClick: () => navigate("/orders"),
    },
    {
      Icon: ReportIcon,
      title: "Report Issue",
      desc: "Flag a problem with your order",
      onClick: () => setShowModal(true),
    },
  ];

  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
    setComplainType("");
    setIssue("");
  };

  const handleSubmit = async () => {
    if (!complainType)    { toast.warning("Please select a complaint type"); return; }
    if (!issue.trim())    { toast.warning("Please describe your issue");     return; }
    setSubmitting(true);
    try {
      await submitHelpCenterComplaint({ complain_type: complainType, issue });
      toast.success("Complaint submitted! We'll get back to you soon.");
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit complaint");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="hp-root">

      {/* ── Hero ── */}
      <div className="hp-hero">
        <div className="hp-hero-inner">
          <h1 className="hp-hero-title">Hello. How can we help?</h1>
          <p className="hp-hero-sub">Search our knowledge base or browse topics below.</p>

          <form className="hp-search-form" onSubmit={(e) => e.preventDefault()}>
            <span className="hp-search-icon"><SearchIcon/></span>
            <input
              type="text"
              className="hp-search-input"
              placeholder="Search for answers…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className="hp-search-clear" onClick={() => setSearchQuery("")}>✕</button>
            )}
          </form>
        </div>
      </div>

      {/* ── Quick Actions ── */}
      <section className="hp-section">
        <h2 className="hp-section-title">Quick Actions</h2>
        <div className="hp-actions-grid">
          {QUICK_ACTIONS.map(({ Icon, title, desc, onClick }) => (
            <button key={title} className="hp-action-card" type="button" onClick={onClick}>
              <div className="hp-action-icon"><Icon/></div>
              <div className="hp-action-text">
                <div className="hp-action-title">{title}</div>
                <div className="hp-action-desc">{desc}</div>
              </div>
              <span className="hp-action-arrow"><ArrowIcon/></span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Common Topics ── */}
      <section className="hp-section">
        <h2 className="hp-section-title">Common Topics</h2>
        <div className="hp-topics-grid">
          {TOPICS.map(({ Icon, title, desc, count }) => (
            <div key={title} className="hp-topic-card">
              <div className="hp-topic-icon"><Icon/></div>
              <h3 className="hp-topic-title">{title}</h3>
              <p className="hp-topic-desc">{desc}</p>
              <button className="hp-topic-link" type="button">
                See all {count} articles <ArrowIcon/>
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── Contact strip ── */}
      <div className="hp-contact-strip">
        <div className="hp-contact-text">
          <div className="hp-contact-title">Still need help?</div>
          <div className="hp-contact-sub">Our support team is available Mon–Fri, 9am–6pm.</div>
        </div>
        <button className="hp-contact-btn" type="button" onClick={() => navigate("/contact")}>
          Contact Support
        </button>
      </div>

      {/* ── Report Issue Modal ── */}
      {showModal && (
        <div className="hp-overlay" onClick={closeModal}>
          <div className="hp-modal" onClick={(e) => e.stopPropagation()}>

            <div className="hp-modal-head">
              <span className="hp-modal-title">Report an Issue</span>
              <button className="hp-modal-close" onClick={closeModal} disabled={submitting}>
                <CloseIcon/>
              </button>
            </div>

            <div className="hp-modal-body">
              <div className="hp-field">
                <label className="hp-label">Complaint Type <span className="hp-req">*</span></label>
                <div className="hp-select-wrap">
                  <select
                    className="hp-select"
                    value={complainType}
                    onChange={(e) => setComplainType(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">Select a type…</option>
                    {COMPLAINT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <span className="hp-sel-arrow"><ChevronDown/></span>
                </div>
              </div>

              <div className="hp-field">
                <label className="hp-label">Describe your issue <span className="hp-req">*</span></label>
                <textarea
                  className="hp-textarea"
                  placeholder="What went wrong? Please be as specific as possible…"
                  rows={5}
                  value={issue}
                  onChange={(e) => setIssue(e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="hp-modal-foot">
              <button className="hp-btn-ghost" onClick={closeModal} disabled={submitting}>Cancel</button>
              <button
                className="hp-btn-primary"
                onClick={handleSubmit}
                disabled={submitting || !complainType || !issue.trim()}
              >
                {submitting
                  ? <><span className="hp-btn-spin"/>Submitting…</>
                  : <><SendIcon/>Submit</>
                }
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}